/**
 * Thin typed client for the avatar store's WEB reads under `/api/web/v2/avatars`
 * (epic 2026-10-06-search-candidates-reports, sub-task 05):
 *
 *   - `resolveThrive` — Thrive photos by CONTACT id, for the Candidates tab and
 *     the candidate panel: per id `photo` (with the stored key), `none` or
 *     `pending`, plus how long to wait before asking again.
 *   - `resolveLinkedin` — the existing `/avatars/resolve` by LinkedIn URL, the
 *     fallback for a contact Thrive answered `none` for.
 *   - `presignAvatars` — the presign fallback `useStoredImageUrls` uses when
 *     the media session does not serve avatars from CloudFront.
 *
 * `imports.client.ts` and `companyLists.client.ts` each carry their own
 * `presignAvatars`; this one exists so the searches feature does not have to
 * import the imports client (another bounded context) for a photo. Same
 * chunking rule as theirs — see `PRESIGN_MAX_KEYS_PER_REQUEST`.
 *
 * Cookie auth rides the shared `makeAuthedFetch` lifecycle helper, like every
 * other web client.
 */

import type {
  AvatarPresignResponse,
  AvatarResolveResponse,
  ThriveAvatarResolveResponse,
} from "@nu-assist/contracts";
import { makeAuthedFetch } from "@nu-assist/shared-fe";

type FetchLike = (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;

export interface AvatarsClientConfig {
  apiBase: string;
  fetchImpl?: FetchLike;
}

export class AvatarsApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly url: string
  ) {
    super(`Avatars request failed (${status}) for ${url}`);
    this.name = "AvatarsApiError";
    Object.setPrototypeOf(this, AvatarsApiError.prototype);
  }
}

/**
 * Max stored-avatar keys per `/avatars/presign` request — 50, as the imports
 * and company-lists clients: a LinkedIn key is ~76 bytes, so 100 in one body
 * sits a few hundred bytes under the shared ALB WAF's ~8 KB cap, and 50 keeps a
 * comfortable margin. Must stay ≤ the server's `max_length`.
 */
const PRESIGN_MAX_KEYS_PER_REQUEST = 50;

/** Max contact ids per `/avatars/resolve-thrive` request — the server's cap is
 *  100; a page is ≤50, so this is one request per page. */
export const RESOLVE_THRIVE_MAX_IDS_PER_REQUEST = 100;

export function createAvatarsClient(cfg: AvatarsClientConfig) {
  const authedFetch: FetchLike =
    cfg.fetchImpl ??
    makeAuthedFetch({
      apiBase: cfg.apiBase,
      onSessionExpired: () => window.location.assign("/login"),
    });

  async function post<T>(path: string, body: unknown, signal?: AbortSignal): Promise<T> {
    const url = `${cfg.apiBase}/api/web/v2/avatars${path}`;
    const res = await authedFetch(url, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      signal,
    });
    if (!res.ok) throw new AvatarsApiError(res.status, url);
    return (await res.json()) as T;
  }

  return {
    /**
     * POST /avatars/resolve-thrive — Thrive photos by contact id.
     *
     * `enqueueMissing` is true on the FIRST call for a set of ids (the server
     * enqueues one fetch per unknown id) and false on every poll, so a slow
     * worker is not asked twice for the same contact. Chunked at the server's
     * cap; the per-id maps are merged and the longest refresh hint kept.
     */
    async resolveThrive(
      contactIds: readonly string[],
      { enqueueMissing = true, signal }: { enqueueMissing?: boolean; signal?: AbortSignal } = {}
    ): Promise<ThriveAvatarResolveResponse> {
      const batches: string[][] = [];
      for (let i = 0; i < contactIds.length; i += RESOLVE_THRIVE_MAX_IDS_PER_REQUEST) {
        batches.push([...contactIds.slice(i, i + RESOLVE_THRIVE_MAX_IDS_PER_REQUEST)]);
      }
      const responses = await Promise.all(
        batches.map((batch) =>
          post<ThriveAvatarResolveResponse>(
            "/resolve-thrive",
            { contact_ids: batch, enqueue_missing: enqueueMissing },
            signal
          )
        )
      );
      const merged: ThriveAvatarResolveResponse = { results: {}, refresh_after_seconds: null };
      for (const r of responses) {
        Object.assign(merged.results, r.results);
        if (r.refresh_after_seconds != null) {
          merged.refresh_after_seconds = Math.max(
            merged.refresh_after_seconds ?? 0,
            r.refresh_after_seconds
          );
        }
      }
      return merged;
    },

    /** POST /avatars/resolve — the LinkedIn store, by profile URL. The fallback
     *  for contacts Thrive has no photo for; a fresh row carries the stored key. */
    async resolveLinkedin(
      linkedinUrls: readonly string[],
      signal?: AbortSignal
    ): Promise<AvatarResolveResponse> {
      if (linkedinUrls.length === 0) return { known: {} };
      return post<AvatarResolveResponse>("/resolve", { linkedin_urls: [...linkedinUrls] }, signal);
    },

    /** POST /avatars/presign — short-lived GET URLs for stored avatar keys, in
     *  batches of `PRESIGN_MAX_KEYS_PER_REQUEST`. A failed batch leaves its
     *  keys out of the map (those contacts render initials); every batch
     *  failing rethrows the first error so the caller can log it. */
    async presignAvatars(s3Keys: string[]): Promise<Record<string, string>> {
      const batches: string[][] = [];
      for (let i = 0; i < s3Keys.length; i += PRESIGN_MAX_KEYS_PER_REQUEST) {
        batches.push(s3Keys.slice(i, i + PRESIGN_MAX_KEYS_PER_REQUEST));
      }
      const results = await Promise.allSettled(
        batches.map((batch) => post<AvatarPresignResponse>("/presign", { s3_keys: batch }))
      );
      const urls: Record<string, string> = {};
      let anyFulfilled = false;
      for (const r of results) {
        if (r.status === "fulfilled") {
          anyFulfilled = true;
          Object.assign(urls, r.value.urls);
        }
      }
      if (!anyFulfilled && results.length > 0) {
        const firstRejected = results.find((r) => r.status === "rejected");
        throw firstRejected?.reason ?? new AvatarsApiError(0, "/avatars/presign");
      }
      return urls;
    },
  };
}

export type AvatarsClient = ReturnType<typeof createAvatarsClient>;
